import {
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type RefObject,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {
  DEFAULT_LAUNCHER_PLACEMENT,
  LAUNCHER_ACTION_GAP,
  LAUNCHER_ACTION_SIZE,
  LAUNCHER_SIZE,
  type LauncherBounds,
  launcherFraction,
  launcherPixels,
  type LauncherPlacement,
  type LauncherPoint,
} from './launcherPlacement';

export interface LauncherPlacementChange {
  action: 'dock' | 'undock' | 'move';
  method: 'pointer' | 'keyboard';
}

interface Options {
  boundary?: RefObject<HTMLElement | null>;
  headerContainer?: HTMLElement | null;
  initialPlacement?: LauncherPlacement;
  value?: LauncherPlacement;
  onChange?: (value: LauncherPlacement, change: LauncherPlacementChange) => void;
  bottomInset?: number;
  dockingAvailable?: boolean;
}

export function useJotLauncherPlacement({
  boundary,
  headerContainer,
  initialPlacement = DEFAULT_LAUNCHER_PLACEMENT,
  value,
  onChange,
  bottomInset = 0,
  dockingAvailable = true,
}: Options = {}) {
  const [internalPlacement, setInternalPlacement] = useState<LauncherPlacement | null>(null);
  const placement = value ?? internalPlacement ?? initialPlacement;
  const docked = placement.docked && dockingAvailable;
  const [bounds, setBounds] = useState<LauncherBounds>({
    left: 16,
    top: 120,
    right: 16,
    bottom: 120,
  });
  const [dragPoint, setDragPoint] = useState<LauncherPoint | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const suppressClick = useRef(false);
  const drag = useRef<{
    pointer: number;
    origin: LauncherPoint;
    start: LauncherPoint;
    point: LauncherPoint | null;
  } | null>(null);
  const position = launcherPixels(dragPoint ?? placement.point, bounds);

  useLayoutEffect(() => {
    const measure = () => {
      const rect = boundary?.current?.getBoundingClientRect();
      const header = headerContainer?.closest('header')?.getBoundingClientRect();
      const left = Math.max(16, (rect?.left ?? 0) + 16);
      const bottom = Math.max(
        8,
        Math.min(window.innerHeight, rect?.bottom ?? window.innerHeight) -
          LAUNCHER_SIZE -
          Math.max(bottomInset, LAUNCHER_ACTION_GAP + LAUNCHER_ACTION_SIZE + 4),
      );
      setBounds({
        left,
        top: Math.min(bottom, Math.max(8, (rect?.top ?? header?.bottom ?? 64) + 8)),
        right: Math.max(
          left,
          Math.min(window.innerWidth, rect?.right ?? window.innerWidth) - LAUNCHER_SIZE - 16,
        ),
        bottom,
      });
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (boundary?.current) observer?.observe(boundary.current);
    const header = headerContainer?.closest('header');
    if (header) observer?.observe(header);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [boundary, headerContainer, bottomInset]);

  function update(next: LauncherPlacement, change: LauncherPlacementChange): void {
    if (value === undefined) setInternalPlacement(next);
    onChange?.(next, change);
  }

  function setDocked(docked: boolean, method: LauncherPlacementChange['method'] = 'pointer'): void {
    if (docked === placement.docked) return;
    update({ ...placement, docked }, { action: docked ? 'dock' : 'undock', method });
    setAnnouncement(
      docked ? 'Jot docked in the header.' : 'Jot returned to its previous floating position.',
    );
  }

  function onPointerDown(event: PointerEvent<HTMLButtonElement>): void {
    if (docked || event.button !== 0 || drag.current) return;
    suppressClick.current = false;
    drag.current = {
      pointer: event.pointerId,
      origin: { x: event.clientX, y: event.clientY },
      start: position,
      point: null,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function onPointerMove(event: PointerEvent<HTMLButtonElement>): void {
    const current = drag.current;
    if (!current || current.pointer !== event.pointerId) return;
    const dx = event.clientX - current.origin.x;
    const dy = event.clientY - current.origin.y;
    if (!current.point && Math.hypot(dx, dy) < 6) return;
    suppressClick.current = true;
    current.point = launcherFraction({ x: current.start.x + dx, y: current.start.y + dy }, bounds);
    setDragPoint(current.point);
  }

  function finishDrag(event: PointerEvent<HTMLButtonElement>): void {
    const current = drag.current;
    if (!current || current.pointer !== event.pointerId) return;
    if (
      current.point &&
      event.type === 'pointerup' &&
      (current.point.x !== placement.point.x || current.point.y !== placement.point.y)
    ) {
      update({ ...placement, point: current.point }, { action: 'move', method: 'pointer' });
      setAnnouncement('Jot moved. Its position is remembered.');
    }
    drag.current = null;
    setDragPoint(null);
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
  }

  function onMoveKeyDown(event: KeyboardEvent<HTMLButtonElement>): void {
    if (docked) return;
    const step = event.shiftKey ? 32 : 8;
    const direction: Record<string, LauncherPoint> = {
      ArrowLeft: { x: -step, y: 0 },
      ArrowRight: { x: step, y: 0 },
      ArrowUp: { x: 0, y: -step },
      ArrowDown: { x: 0, y: step },
    };
    const delta = direction[event.key];
    if (!delta) return;
    event.preventDefault();
    const point = launcherFraction({ x: position.x + delta.x, y: position.y + delta.y }, bounds);
    if (point.x === placement.point.x && point.y === placement.point.y) return;
    update({ ...placement, point }, { action: 'move', method: 'keyboard' });
    setAnnouncement('Jot moved. Use arrow keys to adjust its position.');
  }

  function consumeDragClick(event: MouseEvent<HTMLButtonElement>): boolean {
    // Pointer release can click the launcher even after dragging back to its starting point.
    // Keyboard activation (detail 0) must still open support after a drag or cancellation.
    const wasDrag = !docked && event.detail !== 0 && suppressClick.current;
    suppressClick.current = false;
    return wasDrag;
  }

  return {
    docked,
    position,
    dragging: dragPoint !== null,
    announcement,
    setDocked,
    onMoveKeyDown,
    consumeDragClick,
    dragHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: finishDrag,
      onPointerCancel: finishDrag,
      onLostPointerCapture: finishDrag,
    },
    reset: () => update(initialPlacement, { action: 'move', method: 'pointer' }),
  };
}

export type JotLauncherPlacementController = ReturnType<typeof useJotLauncherPlacement>;
